import React, { useState, useEffect } from "react";

import { IActionHandler, Sort } from "@preveil-api";
import {
  Account, CollectionEntity, useAppDispatch, GlobalErrorMessages, Message, MessageHandlerDisplayType, MessageAnchors, useDriveSearch, EntryItem, DirectoryEntity,
  DriveSearchFilterType, DriveSearchFilterTypes, SORT_DIRECTION, SearchNode, SearchFieldType, DriveSearchFilters, DriveSearchFilter, DriveSearchFilterSet, DirectoryPaginationInfo, SearchClient,
  SearchErrorCodes
} from "src/common";
import { SearchWrapper, SearchForm, SearchDropdown } from ".";
import { uiActions, driveActions } from "src/store";

type AllProps = {
    search_client: SearchClient;
    current_account: Account;
    collection_info: CollectionEntity;
    current_directory: DirectoryEntity;
    pagination_info?: DirectoryPaginationInfo;
    selected: string[];
}

// const debounce_time = 500;
const search_filters = new DriveSearchFilters();
function DriveSearchComponent(props: AllProps) {
    const { search_client, current_account, current_directory, collection_info, pagination_info } = props;
    const dispatch = useAppDispatch();
    const [active, setActive] = useState<boolean>(false);
    const [search_term, setSearchTerm] = useState<string>("");
    const [selected_folders, setSelectedFolders] = useState<EntryItem[]>([]);
    const [sort, setSort] = useState<Sort<keyof SearchNode>>({ field: SearchFieldType.last_modification_date, direction: SORT_DIRECTION.descending });
    const [filters, setFilters] = useState<DriveSearchFilterSet>({});
    const { initSearch, resetSearch, pauseSearch, destroySearch, results, paused, loading, progress, error } = useDriveSearch(current_account, collection_info, search_client, search_term);

    // Description:  Catch changes to search_term state (input)
    useEffect(() => {
        if (!!search_term) {
            if (!active) {
                setActive(true);
                initSearch(current_directory, pagination_info);
            } else {
                pauseSearch(false);
            }
        }
    }, [search_term]);

    // Description: Handle Search state and actions
    const DriveSearchRequests = {
        // Description: Reset, Pause or restart Search (next batch): 
        //              Pause clears the search term but pauses query
        //              Reset clears all and returns to initial state
        handleReset: (params: { error?: SearchErrorCodes, pause?: boolean }) => {
            setSearchTerm("");
            if (!!params?.pause) { // NOTE: Pause search on clear of the searchbox "X"
                pauseSearch(true);
            } else {
                setActive(false);
                setSelectedFolders([]);
                resetSearch();
                search_filters.initialize();
                setFilters(search_filters.filter_set);
                !!paused && pauseSearch(false);
            }
        },
        handleDestroy() {
            destroySearch();
            setSearchTerm("");
            setActive(false);
        },
        handlePause: (pause: boolean = false) => {
            pauseSearch(pause);
        },
        handleSetSort: setSort,
        handleSetFilter: (_filter_set: DriveSearchFilter) => {
            search_filters.modifyFilterSet(_filter_set);
            setFilters(search_filters.filter_set);
        },
        handleResetFilters: (params: DriveSearchFilterTypes) => {
            // NOTE: Clear selected to reset parent scoped filters
            params === DriveSearchFilterType.parent_scope && dispatch(driveActions.setSelected([]));
            if (params === DriveSearchFilterType.fs_type) {
                search_filters.resetFSTypeFilter();
                search_filters.resetExtension();
            }
            params === DriveSearchFilterType.extension && search_filters.resetExtension();
            setFilters(search_filters.filter_set);
        },
        handlePageErrorMessage: (params: { message: string, stack?: any }) => {
            dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
        },
    };

    //  Description: Handle all actions from Children forms
    function handlePageActions(action_obj: IActionHandler) {
        const callback = `handle${action_obj.actionType}`;
        if ((DriveSearchRequests as any)[callback] instanceof Function) {
            (DriveSearchRequests as any)[callback](action_obj.params);
        } else {
            const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
            DriveSearchRequests.handlePageErrorMessage({ message, stack: action_obj });
        }
    }

    return <SearchWrapper
        active={active}
        handleAction={handlePageActions}>
        <div className="search-box">
            <SearchForm
                search_term={search_term}
                active={active}
                paused={paused}
                loading={loading}
                setSearchTerm={setSearchTerm}
                handleAction={handlePageActions} />
            {
                active &&
                <SearchDropdown
                    current_account={current_account}
                    search_term={search_term}
                    results={results}
                    sort={sort}
                    filters={filters}
                    progress={progress}
                    paused={paused}
                    loading={loading}
                    error={error}
                    selected_folders={selected_folders}
                    handleAction={handlePageActions} />
            }
        </div>
    </SearchWrapper>;
}

export default React.memo(DriveSearchComponent);
